import { styled, darken } from '@mui/material/styles';
import { AppBar, Button, IconButton, ListItemText, MenuItem, Tab, Tabs } from '@mui/material';
import Modal from 'global/components/modal';
import { isDark } from 'global/styles';

export const NAV_ACTIVE_CLASS = 'nav-active';

// PFM-2628: id of the header slot into which the mobile-only Share/Download/
// Print cluster is portaled (inline with Location/Status, right-aligned).
// Lives here (a leaf styles module) so both the NavBar header and the consumer
// (ReportTabs) can import it without a circular dependency.
export const ASSESSMENT_HEADER_ACTIONS_SLOT_ID = 'assessment-header-actions';

export const NavBarRoot = styled(AppBar, {
  shouldForwardProp: (p) => p !== 'navHidden',
})(({ theme, navHidden }) => ({
  flexGrow: 1,
  ...(!isDark(theme)
    ? {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
      }
    : {}),
  '@media print': {
    display: 'none',
  },
  '& h6': {
    width: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
  ...(navHidden ? { display: 'none' } : {}),
}));

export const NavBarContentRootWrap = styled('div')({
  // Headers must not grow inside flex document panels (avoids white gap above tabs).
  flexGrow: 0,
  flexShrink: 0,
  '@media print': {
    display: 'none',
  },
  '& h6': {
    width: '100%',
    overflowX: 'auto',
    whiteSpace: 'nowrap',
  },
});

export const NavBarContentRoot = styled(AppBar)(({ theme }) => {
  const chrome = isDark(theme) ? theme.palette.primary.dark : theme.palette.primary.main;

  return {
    background: `linear-gradient(to bottom, ${darken(chrome, 0.175)}, ${darken(chrome, 0.375)})`,
    color: theme.palette.getContrastText(chrome),
  };
});

export const NavBarContentActions = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  gap: theme.spacing(1),
}));

export const NavBarContentChildren = styled('div')({
  minWidth: 0,
  flex: 1,
  display: 'flex',
  alignItems: 'center',
});

// PFM-2628: wraps the assessment field row and the mobile action cluster so
// Share/Download/Print sit on the same line as Location/Status (right-aligned)
// from `lg` down, instead of stacking in their own row above the fields.
export const NavBarContentMetaRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'nowrap',
  width: '100%',
});

// PFM-2628: container for the mobile-only Share/Download/Print cluster that is
// portaled in on the same row as Location/Status, right-aligned. Hidden at `lg`+
// (desktop keeps the top-right overlay). `margin-left: auto` keeps the cluster
// on the right even when the field row is missing (assessment templates).
export const NavBarContentActionsRow = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexShrink: 0,
  marginLeft: 'auto',
  boxSizing: 'border-box',
  paddingRight: theme.spacing(2),
  // Match ReportAssessment field-row padding so icons align with Location/Status
  // and templates still have space under the title before the tabs.
  paddingBottom: theme.spacing(3),
  [theme.breakpoints.up('lg')]: {
    display: 'none',
  },
}));

export const NavBarGrow = styled('div')({
  flexGrow: 1,
});

export const NavBarMenuButton = styled(IconButton, {
  shouldForwardProp: (p) => p !== 'sidebar',
})(({ theme, sidebar }) => ({
  marginLeft: -12,
  ...(sidebar
    ? {}
    : {
        marginRight: theme.spacing(1),
      }),
}));

export const NavBarTaskButton = styled('div')(({ theme }) => ({
  height: theme.spacing(2),
  minWidth: theme.spacing(16),
  flexShrink: 0,
  [theme.breakpoints.down('md')]: {
    minWidth: theme.spacing(8),
  },
}));

export const NavHeaderLogoWrapper = styled('a')({
  width: '100%',
  height: 120,
  display: 'flex',
  alignItems: 'center',
  // PFM-2625: center the logo horizontally within the drawer header. Builder
  // logos (e.g. Toll Brothers wordmark, round Redwood mark) sized themselves to
  // the top-left corner because the wrapper was shrink-to-content with no
  // horizontal justification; full width + centered justify restores the
  // centered IBACOS-style alignment for logos of any aspect ratio.
  justifyContent: 'center',
  // The logo renders through EImage (global/components/image), which wraps the
  // <img> in a fixed-width `.IMAGE` container holding an inner flex `> div`.
  // That inner div has no horizontal justification (global default flex-start),
  // so the visible <img> was pinned to the left of the .IMAGE container even
  // though the container itself was centered in this wrapper. Scope the centering
  // to this drawer logo only (do NOT touch the global EImage styles): stretch the
  // container to full width and center its inner flex content so the actual image
  // sits in the middle of the drawer header for any aspect ratio.
  '& .IMAGE > div': {
    justifyContent: 'center',
    // The inner EImage div carries a global horizontal margin (spacing) that made
    // it overflow the full-width container and shift the centered image ~8px right.
    // Zero the horizontal margin so the visible image is truly centered.
    marginLeft: 0,
    marginRight: 0,
  },
  // PFM-2625 follow-up: the rendered drawer logo <img> (EImage renders it under
  // the `.IMAGE` container) carries inline width:100%/height:100% with the
  // browser-default object-fit:fill. The `.IMAGE` box constrains width (200px)
  // and height (100px) on INDEPENDENT axes, so a wide wordmark (e.g. Pulte,
  // Conner Homes — natural ~6:1) gets stretched to fill the ~2:1 box and looks
  // squished. Force object-fit:contain on just this drawer logo image so it
  // scales down within the box preserving aspect ratio (e.g. 200×~33, centered)
  // instead of filling it. Scoped to the NavBar drawer — the global EImage
  // styles are untouched.
  '& .IMAGE img': {
    objectFit: 'contain',
  },
});

export const NavHeaderLogo = styled('img')(({ theme }) => ({
  margin: theme.spacing(1),
  maxHeight: '100px',
  maxWidth: '200px',
  objectFit: 'contain',
}));

export const NavPerformLogo = styled('img')({
  margin: '24px auto',
  maxWidth: '100px',
});

export const NavCopyright = styled('div')(({ theme }) => ({
  display: 'block',
  fontSize: '11px',
  margin: 'auto',
  marginTop: theme.spacing(2),
}));

export const NavMenuTitle = styled(ListItemText)({
  textTransform: 'capitalize',
});

// Active state is applied via NavLink activeClassName={NAV_ACTIVE_CLASS}.
export const NavMenuItem = styled(MenuItem)(({ theme }) => ({
  [`&.${NAV_ACTIVE_CLASS}`]: {
    backgroundColor: theme.palette.action.hover,
    fontWeight: 'bold',
  },
}));

// Compact (icon-only) treatment is CSS-driven below lg. Match the alerts bell:
// white icon, transparent fill in both light and dark — do not use primary fill
// when a location is set (`primary.dark` reads as a brand-color change in dark mode).
export const NavLocationButton = styled(Button)(({ theme }) => ({
  backgroundColor: theme.palette.primary.dark,
  minWidth: 'auto',
  gap: theme.spacing(1),
  whiteSpace: 'nowrap',
  '& .nav-location-label': {
    whiteSpace: 'nowrap',
  },
  [theme.breakpoints.down('lg')]: {
    gap: 0,
    minWidth: 40,
    width: 40,
    height: 40,
    padding: 0,
    justifyContent: 'center',
    alignItems: 'center',
    '&&, &&:hover': {
      color: theme.palette.common.white,
    },
    '&&': {
      backgroundColor: 'transparent',
    },
    '&&:hover': {
      backgroundColor: 'rgba(255, 255, 255, 0.12)',
    },
    '& .nav-location-label': {
      display: 'none',
    },
  },
}));

// Plain span (not MUI Icon) so SvgIcons from getRealmIcon center cleanly.
export const NavLocationIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  lineHeight: 0,
  '& > svg': {
    fontSize: '1.25rem',
  },
});

// Below-lg banner when a location is set. Hidden on lg+ via CSS so the control
// doesn't need a media-query hook. Height is published to
// --nav-location-banner-height (0 when display:none) so PrintView overlays
// can sit below it.
export const NavLocationBanner = styled('div')(({ theme }) => ({
  display: 'none',
  alignItems: 'center',
  gap: theme.spacing(1),
  padding: theme.spacing(0.75, 2),
  backgroundColor: isDark(theme) ? theme.palette.primary.dark : darken(theme.palette.primary.main, 0.2),
  color: theme.palette.primary.contrastText,
  fontSize: theme.typography.body2.fontSize,
  [theme.breakpoints.down('lg')]: {
    display: 'flex',
  },
}));

export const NavLocationBannerText = styled('span')({
  flex: 1,
  minWidth: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
});

export const NavLocationBannerReset = styled(IconButton)(({ theme }) => ({
  color: 'inherit',
  marginRight: theme.spacing(-1),
  padding: theme.spacing(0.5),
}));

export const NavTabIcon = styled('span')({
  display: 'inline-flex',
  alignItems: 'center',
  flexShrink: 0,
});

// Collapses below lg; expands for every tab on lg+, and for the selected tab below lg.
const navTabLabelVisible = (theme) => ({
  maxWidth: 200,
  opacity: 1,
  marginLeft: theme.spacing(1),
});

export const NavTabLabel = styled('span')(({ theme }) => ({
  overflow: 'hidden',
  whiteSpace: 'nowrap',
  textTransform: 'capitalize',
  maxWidth: 0,
  opacity: 0,
  marginLeft: 0,
  transition: theme.transitions.create(['max-width', 'opacity', 'margin'], {
    duration: 280,
    easing: theme.transitions.easing.easeInOut,
  }),
  [theme.breakpoints.up('lg')]: navTabLabelVisible(theme),
  '.Mui-selected &': navTabLabelVisible(theme),
}));

export const NavTabs = styled(Tabs)(({ theme }) => ({
  '& .MuiTabs-indicator': {
    backgroundColor: theme.palette.secondary.main,
  },
  // Below md: wayfinding only — show the active tab name, ignore clicks.
  [theme.breakpoints.down('md')]: {
    '& .MuiTab-root:not(.Mui-selected)': {
      display: 'none',
    },
    '& .MuiTab-root': {
      pointerEvents: 'none',
      minWidth: 'auto',
      paddingLeft: theme.spacing(1),
      paddingRight: theme.spacing(1),
    },
    '& .MuiTabs-indicator': {
      display: 'none',
    },
  },
}));

export const NavTab = styled(Tab)(({ theme }) => ({
  flexDirection: 'row',
  display: 'inline-flex',
  alignItems: 'center',
  color: 'inherit',
  opacity: 0.75,
  minHeight: 48,
  '&.Mui-selected': {
    color: 'inherit',
    opacity: 1,
  },
  [theme.breakpoints.between('md', 'lg')]: {
    minWidth: 48,
    paddingLeft: theme.spacing(1.5),
    paddingRight: theme.spacing(1.5),
  },
  // Overlay tab for non-primary pages (profile, account, etc.) — display-only.
  '&.nav-tab-overlay': {
    pointerEvents: 'none',
    opacity: 1,
  },
}));

// Header reset is desktop-only; compact widths use the banner reset instead.
export const NavResetButton = styled(Button)(({ theme }) => ({
  minWidth: 'inherit',
  width: theme.spacing(5),
  [theme.breakpoints.down('lg')]: {
    display: 'none',
  },
}));

export const NavNewLink = styled('a')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

export const NavRealmModal = styled(Modal)(({ theme }) => ({
  '& .MuiDialog-paperScrollPaper': {
    height: '100%',
  },
  // List rows already have ListItemButton gutters; drop DialogContent inset
  // so the picker can use the full modal width on small screens.
  [theme.breakpoints.down('sm')]: {
    '& .MuiDialogContent-root': {
      paddingLeft: 0,
      paddingRight: 0,
    },
  },
}));
